import { apiRequest } from "@/lib/queryClient";
import { authStore } from "@/lib/authStore";
import { designTokens, notificationSeverityTokens, type NotificationSeverityName } from "@/lib/tokens";
import type { ExtendedUser } from "@/lib/oidcConfig";
import { deriveTenantScope } from "@/lib/tenantScope";
import type { LucideIcon } from "lucide-react";
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Info,
  Octagon,
} from "lucide-react";

const DEFAULT_TENANT = import.meta.env.VITE_DEV_TENANT_ID || 'default';

const resolveTenantScopeState = (explicitTenant?: string) => {
  const user = authStore.getUser?.() as ExtendedUser | null;
  const scope = deriveTenantScope(user, explicitTenant || null);
  return {
    slug: scope.platformSlug || DEFAULT_TENANT,
    uuid: scope.platformUuid,
  };
};

export interface StoredNotification {
  id: string;
  tenant_id: string;
  title: string;
  message: string;
  severity: 'success' | 'warning' | 'error' | 'info' | 'critical';
  category: string;
  action_url?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at: string;
  read: boolean;
  copy?: {
    event?: string | null;
    impact?: string | null;
    next_step?: string | null;
    nextStep?: string | null;
  } | null;
  reference_id?: string | null;
}

export interface NotificationsResponse {
  tenant_id: string;
  items: StoredNotification[];
}

export type NotificationSeverity = NotificationSeverityName;

export interface NotificationVisual {
  icon: LucideIcon;
  iconColor: string;
  surface: string;
  border: string;
  text: string;
  accent: string;
  paddingX: string;
  paddingY: string;
  radius: string;
  shadow: string;
}

const notificationIcons: Record<NotificationSeverityName, LucideIcon> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  error: AlertOctagon,
  critical: Octagon,
};

const defaultPaddingX = designTokens.spacing.rem.md;
const defaultPaddingY = designTokens.spacing.rem.sm;

const notificationVisualMap = Object.freeze(
  (Object.keys(notificationSeverityTokens) as NotificationSeverityName[]).reduce<
    Record<NotificationSeverityName, NotificationVisual>
  >((acc, severity) => {
    const tokens = notificationSeverityTokens[severity];
    acc[severity] = {
      icon: notificationIcons[severity],
      iconColor: tokens.icon,
      surface: tokens.surface,
      border: tokens.border,
      text: tokens.text,
      accent: severity === "critical" ? designTokens.accentRule.brand : tokens.accent,
      paddingX: defaultPaddingX,
      paddingY: defaultPaddingY,
      radius: designTokens.radius.md,
      shadow: designTokens.shadow.soft,
    };
    return acc;
  }, {} as Record<NotificationSeverityName, NotificationVisual>),
);

export const NotificationVisualMap = notificationVisualMap;

export type NotificationVisualMapType = typeof NotificationVisualMap;

export const resolveNotificationVisual = (
  severity?: string | null,
): NotificationVisual => {
  if (!severity) {
    return NotificationVisualMap.info;
  }
  const normalized = severity.toLowerCase() as NotificationSeverityName;
  if (normalized in NotificationVisualMap) {
    return NotificationVisualMap[normalized];
  }
  return NotificationVisualMap.info;
};

export const resolveTenantId = (explicitTenant?: string): string => {
  return resolveTenantScopeState(explicitTenant).slug;
};

export async function fetchNotifications(tenantId?: string): Promise<StoredNotification[]> {
  const scope = resolveTenantScopeState(tenantId);
  const response = await apiRequest('GET', `/api/notifications/${scope.slug}`);
  const data = (await response.json()) as NotificationsResponse;
  return data.items;
}

export async function acknowledgeNotifications(ids: string[], tenantId?: string): Promise<void> {
  const scope = resolveTenantScopeState(tenantId);
  await apiRequest('POST', '/api/notifications/ack', {
    tenant_id: scope.slug,
    tenant_uuid: scope.uuid ?? undefined,
    ids,
  });
}

export async function acknowledgeAllNotifications(tenantId?: string): Promise<void> {
  const scope = resolveTenantScopeState(tenantId);
  await apiRequest('POST', '/api/notifications/ack-all', {
    tenant_id: scope.slug,
    tenant_uuid: scope.uuid ?? undefined,
  });
}
